/* Share a saved master, never a cart or an in-progress upload. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.init(root);
})(typeof window === 'object' ? window : globalThis, function () {
  'use strict';
  const PARAM = 'nh_design';
  const PRODUCTS = new Set(['stickers', 'flag', 'mug', 'beer-mug', 'tshirt', 'hoodie', 'beerdeckel']);

  function normalizeId(value) {
    const id = String(value || '').trim().toUpperCase();
    return /^M-\d{8}-\d{4}-[A-Z0-9]{4}$/.test(id) ? id : '';
  }

  function sharedRequest(href) {
    const params = new URL(href).searchParams;
    const values = params.getAll(PARAM);
    return { present: values.length > 0, id: values.length === 1 ? normalizeId(values[0]) : '' };
  }

  function buildLink(href, mainId, previewThemeId = '') {
    const id = normalizeId(mainId);
    if (!id) throw new Error('Ungültige Design-ID.');
    const source = new URL(href);
    const url = new URL('/pages/editor', source.origin);
    url.searchParams.set(PARAM, id);
    const product = source.searchParams.get('editor_product');
    if (PRODUCTS.has(product)) url.searchParams.set('editor_product', product);
    const preview = source.searchParams.get('preview_theme_id') || String(previewThemeId || '');
    if (/^\d{6,20}$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    return url.href;
  }

  function buildPreviewLink(href, mainId, previewThemeId = '') {
    const url = new URL(buildLink(href, mainId, previewThemeId));
    url.pathname = `/apps/design/${normalizeId(mainId)}`;
    url.searchParams.delete(PARAM);
    return url.href;
  }

  async function verifyPreviewLink(win, url, mainId, imageUrl, signal) {
    let response;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      response = await win.fetch(url, { cache: 'no-store', credentials: 'omit', redirect: 'error', signal });
      if (![404, 503].includes(response.status) || attempt === 3) break;
      // Newly uploaded Shopify images can take a moment to become READY and searchable.
      await abortable(new Promise(resolve => win.setTimeout(resolve, 500 * (2 ** attempt))), signal);
    }
    if (!response.ok || !String(response.headers.get('content-type') || '').includes('text/html')) {
      throw new Error('Die Designvorschau ist noch nicht erreichbar.');
    }
    const html = await response.text();
    const escape = value => String(value).replace(/[&<>"']/g, character => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[character]);
    // This is the public proxy's explicit readiness contract, not a Shopify 404 or login page.
    if (html.length > 96000 ||
        !html.includes(`<meta name="nh:design-id" content="${normalizeId(mainId)}">`) ||
        !html.includes(`<meta property="og:url" content="${escape(url)}">`) ||
        !html.includes(`<meta property="og:image" content="${escape(imageUrl)}">`)) {
      throw new Error('Die Designvorschau ist noch nicht bereit.');
    }
  }

  function abortable(promise, signal) {
    return new Promise((resolve, reject) => {
      const abort = () => reject(Object.assign(new Error('Abgebrochen'), { name: 'AbortError' }));
      if (signal.aborted) { abort(); return; }
      signal.addEventListener('abort', abort, { once: true });
      Promise.resolve(promise).then(value => {
        signal.removeEventListener('abort', abort);
        if (signal.aborted) abort(); else resolve(value);
      }, error => { signal.removeEventListener('abort', abort); reject(error); });
    });
  }

  function init(win) {
    const doc = win.document;
    const editor = doc?.getElementById('nh-editor');
    const modal = doc?.getElementById('nh-design-share-modal');
    if (!editor || !modal || editor.dataset.nhSharingBound) return null;
    const byId = id => doc.getElementById(id);
    const status = byId('nh-design-share-status');
    const input = byId('nh-design-share-link');
    const result = modal.querySelector('[data-nh-share-result]');
    const copy = byId('nh-design-share-copy');
    const native = byId('nh-design-share-native');
    const retry = byId('nh-design-share-retry');
    const loadStatus = byId('nh-shared-design-status');
    const loadMessage = loadStatus?.querySelector('[data-nh-shared-design-message]');
    const loadRetry = byId('nh-shared-design-retry');
    const loadingModal = byId('nh-shared-design-loading-modal');
    const triggers = Array.from(doc.querySelectorAll('[data-nh-share-design]'));
    if (!status || !input || !result || !copy || !native || !retry) return null;
    editor.dataset.nhSharingBound = 'true';
    let saveJob = null;
    let loadJob = null;
    let generation = 0;
    let loadNoticeVersion = 0;
    let link = '';

    function bridge() {
      const api = win.__nhDesignShareBridge;
      if (!api || typeof api.save !== 'function' || typeof api.load !== 'function') {
        throw new Error('Editor wird noch vorbereitet. Bitte versuche es gleich erneut.');
      }
      return api;
    }

    function deadline() {
      const controller = new win.AbortController();
      const timer = win.setTimeout(() => controller.abort(), 45000);
      return { controller, cancel: () => win.clearTimeout(timer) };
    }

    function setSaving(value) {
      modal.setAttribute('aria-busy', String(value));
      triggers.forEach(button => { button.disabled = value; });
      retry.disabled = value;
    }

    function close() {
      generation += 1;
      if (saveJob) { saveJob.controller.abort(); saveJob.cancel(); }
      saveJob = null;
      setSaving(false);
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      win.__nhDesignShareBridge?.closeDialog?.(modal);
    }

    async function save() {
      if (saveJob) return;
      const token = ++generation;
      const job = deadline();
      saveJob = job;
      link = '';
      input.value = '';
      result.hidden = true;
      retry.hidden = true;
      status.hidden = false;
      status.textContent = 'Dein Design wird für den Link gespeichert …';
      setSaving(true);
      try {
        const api = bridge();
        await abortable(api.ready, job.controller.signal);
        if (job.controller.signal.aborted) throw new Error('Abgebrochen');
        const saved = await abortable(api.save({ signal: job.controller.signal }), job.controller.signal);
        if (token !== generation || job.controller.signal.aborted) return;
        if (!saved?.ok || !normalizeId(saved.mainId)) throw new Error('Speichern fehlgeschlagen');
        // A preview link must also work for a recipient without the sender's cookies.
        const theme = win.Shopify?.theme;
        const previewId = theme && !['main', 'live'].includes(theme.role) ? theme.id : '';
        link = buildLink(win.location.href, saved.mainId, previewId);
        let hasPreview = false;
        if (typeof api.prepareSharePreview === 'function') {
          status.textContent = 'Das Vorschaubild für deinen Link wird vorbereitet …';
          try {
            const prepared = await abortable(api.prepareSharePreview(saved.mainId, { signal: job.controller.signal }), job.controller.signal);
            if (token !== generation) return;
            if (job.controller.signal.aborted) throw new Error('Abgebrochen');
            if (prepared?.ok && normalizeId(prepared.mainId) === normalizeId(saved.mainId) && prepared.previewUrl) {
              const candidate = buildPreviewLink(win.location.href, saved.mainId, previewId);
              await abortable(verifyPreviewLink(win, candidate, saved.mainId, prepared.previewUrl, job.controller.signal), job.controller.signal);
              if (token !== generation) return;
              if (job.controller.signal.aborted) throw new Error('Abgebrochen');
              link = candidate;
              hasPreview = true;
            }
          } catch (error) {
            // The saved design remains shareable if the optional image cannot be prepared.
            if (token !== generation) return;
            win.console?.warn?.('[NH Share preview]', error?.message || 'Vorschaubild nicht verfügbar');
          }
        }
        input.value = link;
        result.hidden = false;
        native.hidden = typeof win.navigator.share !== 'function';
        status.hidden = hasPreview || typeof api.prepareSharePreview !== 'function';
        status.textContent = status.hidden ? '' : 'Das Vorschaubild ist gerade nicht verfügbar. Dein Motiv lässt sich über den Link trotzdem öffnen.';
      } catch (error) {
        if (token !== generation) return;
        status.textContent = error?.code === 'REORDER_ONLY'
          ? 'Dieses ältere Design bitte zuerst über seine Main-ID laden.'
          : 'Dein Design konnte gerade nicht für den Link gespeichert werden. Bitte versuche es erneut.';
        retry.hidden = false;
      } finally {
        job.cancel();
        if (token === generation) { saveJob = null; setSaving(false); }
      }
    }

    function open() {
      if (!modal.hidden) return;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      try { bridge().openDialog(modal); }
      catch (_) { modal.focus?.(); }
      void save();
    }

    async function copyLink() {
      if (!link || modal.hidden) return;
      status.hidden = false;
      try {
        if (!win.navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await win.navigator.clipboard.writeText(link);
        status.textContent = 'Link kopiert. Du kannst ihn jetzt verschicken.';
      } catch (_) {
        input.focus();
        input.select();
        input.setSelectionRange?.(0, input.value.length);
        let copied = false;
        try { copied = doc.execCommand?.('copy') === true; } catch (_) { /* Manual copy remains possible. */ }
        status.textContent = copied
          ? 'Link kopiert. Du kannst ihn jetzt verschicken.'
          : 'Der Link ist markiert. Bitte kopiere ihn über das Menü deines Geräts.';
      }
    }

    function shareNative() {
      if (!link || modal.hidden || typeof win.navigator.share !== 'function') return;
      try {
        // Call synchronously from this second click: saving would consume iOS user activation.
        const sharing = win.navigator.share({ title: 'Mein Nett hier. Design', text: 'Schau dir mein Nett hier. Design an:', url: link });
        Promise.resolve(sharing).catch(error => {
          if (!modal.hidden && error?.name !== 'AbortError') {
            status.hidden = false;
            status.textContent = 'Teilen ist hier gerade nicht verfügbar. Du kannst den Link kopieren.';
          }
        });
      } catch (_) {
        status.hidden = false;
        status.textContent = 'Teilen ist hier gerade nicht verfügbar. Du kannst den Link kopieren.';
      }
    }

    async function loadShared() {
      const request = sharedRequest(win.location.href);
      if (!request.present || !loadStatus || !loadMessage || loadJob) return;
      const noticeVersion = ++loadNoticeVersion;
      loadStatus.hidden = false;
      if (loadRetry) loadRetry.hidden = true;
      if (!request.id) {
        loadMessage.textContent = 'Dieser Design-Link ist ungültig. Du kannst hier ein eigenes Motiv gestalten.';
        return;
      }
      const job = deadline();
      loadJob = job;
      loadStatus.hidden = true;
      loadMessage.textContent = 'Geteiltes Design wird geladen …';
      try {
        const api = bridge();
        if (loadingModal) {
          loadingModal.hidden = false;
          loadingModal.setAttribute('aria-hidden', 'false');
          api.openDialog(loadingModal);
        }
        await abortable(api.ready, job.controller.signal);
        if (job.controller.signal.aborted) throw new Error('Abgebrochen');
        await abortable(api.load(request.id, { signal: job.controller.signal }), job.controller.signal);
        if (job.controller.signal.aborted) return;
        loadMessage.textContent = 'Geteiltes Design geladen. Du kannst es bearbeiten und bestellen.';
        win.setTimeout(() => {
          if (noticeVersion === loadNoticeVersion) loadStatus.hidden = true;
        }, 6000);
      } catch (error) {
        if (error?.code === 'LOCAL_CHANGES') {
          loadMessage.textContent = 'Du hast inzwischen selbst gestaltet. Dein aktuelles Motiv bleibt erhalten.';
          // Retrying would silently replace these edits; only a fresh page should import again.
        } else {
          loadMessage.textContent = 'Das geteilte Design konnte nicht geladen werden. Bitte versuche es erneut.';
          if (loadRetry) loadRetry.hidden = false;
        }
      } finally {
        job.cancel();
        loadJob = null;
        if (loadingModal) {
          loadingModal.hidden = true;
          loadingModal.setAttribute('aria-hidden', 'true');
          win.__nhDesignShareBridge?.closeDialog?.(loadingModal);
        }
        loadStatus.hidden = false;
      }
    }

    triggers.forEach(button => button.addEventListener('click', open));
    modal.querySelectorAll('[data-nh-share-close]').forEach(button => button.addEventListener('click', close));
    retry.addEventListener('click', () => { void save(); });
    copy.addEventListener('click', () => { void copyLink(); });
    native.addEventListener('click', shareNative);
    loadRetry?.addEventListener('click', () => { void loadShared(); });
    input.addEventListener('click', () => input.select());
    void loadShared();
    return { open, close, save, loadShared, copyLink, shareNative };
  }

  return { normalizeId, sharedRequest, buildLink, buildPreviewLink, init };
});
